import { Component, HostListener, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { Store } from '@ngrx/store'
import {
  AbstractComponent,
  ChatConversation,
  RoleUserEnum,
  User,
} from 'lib-trend-core'
import {
  bootstrapFailedSelector,
  ChatActions,
  ChatBadgeService,
  ChatConversationDialogsService,
  ChatConversationListComponent,
  ChatConversationListPane,
  ChatConversationListStateService,
  ChatDialogService,
  ChatPresenceView,
  ChatShellService,
  ChatShellTarget,
  chatUnavailableSelector,
  presenceSelector,
  resolveChatAppLaunchTarget,
  resolveChatInitials,
  resolvePresenceView,
  statusesSelector,
  toggleChatStatusDialog,
} from 'lib-trend-core/chat'
import { Observable, combineLatest, takeUntil } from 'rxjs'
import { ChatAppLauncherService } from '../chat-app-launcher/chat-app-launcher.service'

@Component({
  selector: 'chat-bubble-host',
  templateUrl: './chat-bubble-host.component.html',
  styleUrls: ['./chat-bubble-host.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatBubbleHostComponent
  extends AbstractComponent
  implements OnInit
{
  isOpen = false

  popupChannelId: string | null = null

  /** Message the conversation pane should reveal — set by a search hit. */
  popupFocusMessageId: string | null = null

  /**
   * A direct conversation that does not exist yet: "Nova conversa" with someone
   * you have never messaged produces a DRAFT, not a channel. The pane renders it
   * from the user id, and the real conversation joins the list once the first
   * message creates it. Mutually exclusive with `popupChannelId`.
   */
  popupDirectUserId: string | null = null

  currentUser!: User

  /**
   * Gates the "Novo canal" header action. Mirrors the server-side CHANNEL_CREATE
   * guard (channel.service.ts, DEV-1984): platform ADMIN or SUPERVISOR. Groups
   * and directs stay open to every role.
   */
  canCreateChannel = false

  /** Only `dotClass` and `labelKey` are read now — the header shows a dot, not text. */
  myPresence: ChatPresenceView = resolvePresenceView(undefined, true)

  chatUnavailable$: Observable<boolean> = this.store.select(
    chatUnavailableSelector,
  )
  bootstrapFailed$: Observable<boolean> = this.store.select(
    bootstrapFailedSelector,
  )

  /** The mounted conversation list, when one is (compact list or expanded rail). */
  @ViewChild(ChatConversationListComponent)
  private conversationList?: ChatConversationListComponent

  /** Ids of the conversations in the last snapshot — the vanish check reads it. */
  private knownChannelIds = new Set<string>()

  constructor(
    injector: Injector,
    public chatBadge: ChatBadgeService,
    private readonly store: Store,
    private readonly dialog: ChatDialogService,
    private readonly chatShell: ChatShellService,
    private readonly launcher: ChatAppLauncherService,
    private readonly conversationDialogs: ChatConversationDialogsService,
    private readonly listState: ChatConversationListStateService,
  ) {
    super(injector)
  }

  /**
   * Every "open the chat here" instruction in the product lands here — push
   * clicks, the socket-bridge toast, the quick switcher, the status dialog's
   * team shortcut, the topbar entry. This replaced navigating to the retired
   * `/chat` route.
   */
  private handleShellTarget(target: ChatShellTarget): void {
    // A pane or a thread only exists in the chat app: hand the target over (RF-13).
    if (target.view || target.threadRootId) {
      this.launcher.open(resolveChatAppLaunchTarget(null, target))
      return
    }

    this.isOpen = true

    if (target.directUserId) {
      // The draft is already in the store (openChatDirect dispatched it); the compact
      // popup needs the local field flipped.
      this.openDirectDraft(target.directUserId)
      return
    }

    if (target.channelId) {
      this.openChannel(target.channelId, target.messageId ?? null)
    }
  }

  /** Identity card is a toggle for the availability picker (RF-29). */
  openStatusPicker(): void {
    toggleChatStatusDialog(this.dialog)
  }

  /** Global notification preferences — quiet hours + sound (T-21 · RF-36). */
  openPreferences(): void {
    this.conversationDialogs.openPreferences()
  }

  openNewDirect(): void {
    this.conversationDialogs.openNewDirect()
  }

  openNewChannel(): void {
    this.conversationDialogs.openNewChannel()
  }

  /**
   * Saved messages, the team directory and the threads list live in the chat app: the
   * compact window hands the pane over instead of growing a second copy of it (RF-13).
   */
  openFullViewPanel(view: ChatConversationListPane): void {
    this.launcher.open({ view })
    this.minimizePopup()
  }

  /**
   * "Open chat app" — the maximize path (RF-11). Opens the chat app on whatever the
   * compact window is showing and gets out of the way.
   */
  openInChatApp(): void {
    this.launcher.open(resolveChatAppLaunchTarget({
      channelId: this.popupChannelId,
      directUserId: this.popupDirectUserId,
      messageId: this.popupFocusMessageId,
    }))
    this.minimizePopup()
  }

  ngOnInit(): void {
    // The single consumer of the chat shell bus — see ChatShellService.
    this.chatShell.targets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((target) => this.handleShellTarget(target))

    this.chatBadge.init()

    this.currentUser = this.getCurrentUserUser()
    this.canCreateChannel =
      this.currentUser?.roles?.some(
        (role) =>
          role === RoleUserEnum.ADMIN || role === RoleUserEnum.SUPERVISOR,
      ) ?? false

    combineLatest([
      this.chatBadge.conversations$,
      this.store.select(statusesSelector),
      this.store.select(presenceSelector),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([conversations, statuses, presence]) => {
        const previouslyKnownIds = this.knownChannelIds
        this.knownChannelIds = new Set(
          conversations.map((conversation: ChatConversation) => conversation._id),
        )

        // The compact pane must not keep showing a conversation that LEFT the
        // snapshot (deleted by an admin, left from another surface) — the
        // workspace already falls back via the reducer clearing
        // `activeChannelId`, but `popupChannelId` is component-local. Closed
        // only on the known→gone TRANSITION: a channel this list never held
        // is legitimate on screen (a just-created group racing its bootstrap
        // refresh, a public channel opened from a message search hit), so
        // mere absence must never tear the pane down.
        if (
          this.popupChannelId &&
          previouslyKnownIds.has(this.popupChannelId) &&
          !this.knownChannelIds.has(this.popupChannelId)
        ) {
          this.backToList()
        }

        // My own presence: the popup only renders on a live client, so an entry
        // missing from the broadcast map means "online", not "unknown".
        const currentUserId = this.getIDCurrentUser()
        this.myPresence = resolvePresenceView(
          statuses[currentUserId],
          presence[currentUserId] ?? true,
        )
      })

    // What a create dialog produced — see ChatConversationDialogsService.
    this.conversationDialogs.created$
      .pipe(takeUntil(this.destroy$))
      .subscribe((created) => {
        if (created.channelId) {
          this.openChannel(created.channelId)
        } else if (created.directUserId) {
          this.openDirectDraft(created.directUserId)
        }
      })
  }

  /**
   * Esc clears an active search FIRST and only dismisses the panel on a second
   * press — losing the whole panel because you wanted to drop a search term is
   * the kind of thing that stops people from using search at all.
   *
   * It MINIMISES, matching the header's dismiss button: two gestures that mean
   * "get this out of my way" must not leave the panel in two different states.
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.isOpen) return
    // An open Bootstrap dropdown (emoji picker, a row menu, the message
    // overflow) claims this press — its own document listener dismisses it, and
    // dismissing a menu AND minimizing the panel on one key is two surprises.
    // Inner layers that stop propagation (edit textarea, mention menu, thread)
    // never reach here at all.
    if (document.querySelector('.chat-popup .dropdown-menu.show')) return
    // Layered dismissal: the search term clears first, then the window minimizes.
    if (this.listState.searchTerm) {
      this.clearListSearch()
      return
    }
    this.minimizePopup()
  }

  openPopup(): void {
    this.isOpen = true
    // The "Salvas (n)" counter needs the bookmark list, which nothing else in
    // the popup fetches — without this the quick row would always read zero.
    this.store.dispatch(ChatActions.loadSaved())
  }

  minimizePopup(): void {
    this.isOpen = false
  }

  closePopup(): void {
    this.isOpen = false
    this.popupChannelId = null
    this.popupFocusMessageId = null
    this.popupDirectUserId = null
    // Reopening should start from a clean list, not from the last search.
    this.clearListSearch()
  }

  backToList(): void {
    // The term meant "inside that conversation" — carrying it back would filter
    // the list by something the user was not asking about.
    this.clearListSearch()
    this.popupChannelId = null
    this.popupFocusMessageId = null
    this.popupDirectUserId = null
  }

  /** Row click, message hit, shell target or adopted creation. */
  openChannel(
    channelId: string,
    focusMessageId: string | null = null,
  ): void {
    this.clearListSearch()
    this.popupChannelId = channelId
    this.popupFocusMessageId = focusMessageId
    // A real channel always wins over a draft — the draft branch is checked first
    // in the template, so leaving this set would keep it on screen.
    this.popupDirectUserId = null
    this.store.dispatch(ChatActions.openConversation({ channelId }))
  }

  private openDirectDraft(userId: string): void {
    this.popupChannelId = null
    this.popupFocusMessageId = null
    this.popupDirectUserId = userId
  }

  /**
   * The list owns its search, but its term outlives it (the list unmounts while a
   * conversation is open): a mounted list clears itself, otherwise the kept term
   * is reset so the next list starts clean.
   */
  private clearListSearch(): void {
    if (this.conversationList) {
      this.conversationList.clearSearch()
    } else {
      this.listState.searchTerm = ''
    }
  }

  /**
   * Public because the identity card calls it straight from the template for the
   * logged user (no extra field): `currentUser` is read once in `ngOnInit`, so
   * the call is over a constant string on a single element.
   */
  resolveInitials(name: string): string {
    return resolveChatInitials(name)
  }
}
